import Loader from '@app/components/Loader';
import logger from '@app/utils/logger';
import store from '@app/redux/store';
import { AuthState } from '@app/types/auth';
import { Document, DocumentReviewStatus } from '@app/types/document';
import { FormattedMessage } from 'react-intl';
import { forwardRef, Ref, useState } from 'react';
import { getDocument } from '@app/apis/documents';
import { messageContents } from '@app/constants/message';
import { openMessageModal } from '@app/utils/message';
import { useMediaQuery, useTheme } from '@mui/material';

const ViewLink = forwardRef<
  HTMLDivElement,
  {
    data: Document;
    onSuccess?: () => void;
    type?: 'view' | 'view_download';
  }
>(({ data, type, onSuccess }, ref) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isDisabled = [
    DocumentReviewStatus.DRAFT,
    DocumentReviewStatus.MALWARE_DETECTED,
    DocumentReviewStatus.ABANDONED,
  ].includes(data.status_id);

  const handleDocumentView = async (data: Document) => {
    // WeakMap to track window -> cleanup data relationship
    const windowCleanupMap = new WeakMap<
      Window,
      {
        url: string;
        timeoutId: NodeJS.Timeout | null;
        isCleanedUp: boolean;
      }
    >();
    try {
      setIsLoading(true);
      const companyId = (store.getState().auth as AuthState).authContext?.companyId;
      if (!companyId) {
        throw new Error('Company ID is not available in auth context');
      }

      const fileBlob = await getDocument(companyId, data.company_document_id);
      if (!fileBlob || !(fileBlob instanceof Blob)) {
        throw new Error('Invalid file response received');
      }

      const fileURL = URL.createObjectURL(fileBlob);
      const newWindow = window.open(fileURL, '_blank');

      if (newWindow) {
        // Store cleanup data in WeakMap
        const cleanupData = {
          url: fileURL,
          timeoutId: null as NodeJS.Timeout | null,
          isCleanedUp: false,
        };
        windowCleanupMap.set(newWindow, cleanupData);

        const performCleanup = () => {
          const data = windowCleanupMap.get(newWindow);
          if (data && !data.isCleanedUp) {
            URL.revokeObjectURL(data.url);
            if (data.timeoutId) {
              clearTimeout(data.timeoutId);
            }
            data.isCleanedUp = true;
            // Note: WeakMap entry will be automatically cleaned up when window is GC'd
          }
        };

        // Event handlers
        const handleLoad = () => {
          // Small delay to ensure document is rendered
          setTimeout(performCleanup, 1000);
        };

        const handleBeforeUnload = () => {
          performCleanup();
        };

        // Add event listeners
        newWindow.addEventListener('load', handleLoad, { once: true });
        newWindow.addEventListener('beforeunload', handleBeforeUnload, { once: true });

        // Set fallback timeout in the cleanup data
        cleanupData.timeoutId = setTimeout(performCleanup, 10000); // 10 seconds
      } else {
        // If window didn't open (popup blocked), clean up immediately
        URL.revokeObjectURL(fileURL);
      }
      onSuccess && onSuccess();
    } catch (error) {
      logger('error', 'Error while downloading document', error);
      openMessageModal(messageContents.genericErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <Loader style={{ height: isSmallScreen ? '18px' : '32px' }} />
  ) : (
    <span
      ref={ref}
      onClick={isDisabled ? undefined : () => handleDocumentView(data)}
      style={{
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? 'none' : 'auto',
      }}
    >
      <FormattedMessage
        id={type === 'view' ? 'documents.headers.view' : 'documents.actions.view'}
      />
    </span>
  );
});

ViewLink.displayName = 'ViewLink';

export default ViewLink;

export const getDocumentDownloadLink = (
  data: Document,
  type: 'view' | 'view_download',
  onSuccess?: () => void,
  ref?: Ref<HTMLDivElement>,
) => {
  return <ViewLink data={data} onSuccess={onSuccess} type={type} ref={ref} />;
};
